import React from "react";
import { FileText, Calendar, Eye, Download, TrendingUp, TrendingDown, Loader2 } from "lucide-react";
import { COLORS } from "../../constants/colors";

const AssessmentHistory = ({ 
  previousReports, 
  isLoading, 
  error, 
  onViewReport, 
  onDownloadReport, 
  onStartAnalysis 
}) => {
  const styles = {
    card: "bg-white rounded-2xl shadow-sm border border-gray-300",
    icon: "w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0",
    button: "flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-colors border border-gray-300 bg-white",
    smallIcon: "w-8 h-8 rounded-lg flex items-center justify-center"
  };

  const MetricItem = ({ label, value, className = "" }) => (
    <div className="text-center">
      <div className={className}>{value}</div>
      <div className="flex items-center gap-2 justify-center mt-1">
        <span className="text-xs text-gray-500">{label}</span>
      </div>
    </div>
  );

  const ActionButton = ({ onClick, icon, text, className }) => (
    <button onClick={onClick} className={`${styles.button} ${className}`}>
      {icon}
      {text}
    </button>
  );

  return (
    <div className={`${styles.card} p-8`}>
      <div className="flex items-start gap-4 mb-6">
        <div className={styles.icon} style={{ backgroundColor: COLORS.purple }}>
          <FileText className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-gray-900">Assessment History</h2>
          <p className="text-gray-500 text-sm">Track your progress over time</p>
        </div>
        <div className="px-3 py-1 rounded-full text-sm font-medium" style={{ backgroundColor: COLORS.purpleLight, color: COLORS.purpleDark }}>
          {isLoading ? "..." : `${previousReports.length} completed`}
        </div>
      </div>
      
      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center gap-3 text-gray-500">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span>Loading assessment history...</span>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="text-red-500 mb-2">⚠️</div>
            <p className="text-gray-600 mb-4">{error}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && previousReports.length === 0 && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
              <FileText className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Assessments Yet</h3>
            <p className="text-gray-500 mb-4">Start your first skills gap analysis to see your progress here</p>
            <button 
              onClick={onStartAnalysis}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Start Analysis
            </button>
          </div>
        </div>
      )}

      {/* Assessment Reports */}
      {!isLoading && !error && previousReports.length > 0 && (
        <div 
          className="max-h-96 overflow-y-auto space-y-4 pr-2"
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: '#d1d5db #f3f4f6'
          }}
        >
          {previousReports.map((report, index) => (
            <div key={report.id || index} className="bg-gray-50 rounded-xl p-6 border border-gray-300">
              <div className="flex items-center justify-between">
                {/* Left Side - Role and Date */}
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">{report.role}</h4>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Calendar className="w-4 h-4" />
                    {report.date}
                  </div>
                </div>
                
                {/* Center Section - Assessment Metrics */}
                <div className="flex items-center gap-6 flex-1 justify-center">
                  <MetricItem 
                    label="Skills Assessed" 
                    value={<p className="font-bold text-gray-900 text-lg">{report.skillsAssessed}</p>} 
                  />
                  <MetricItem 
                    label="Gaps Identified" 
                    value={<div className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-medium">{report.gapsFound}</div>} 
                  />
                  <MetricItem 
                    label="Overall Score" 
                    value={
                      <div className="flex items-center gap-2">
                        <div className={`${report.scoreColor} text-white px-3 py-1 rounded-full text-sm font-medium`}>{report.overallScore}%</div>
                        {report.trend === "up" ? <TrendingUp className="w-4 h-4 text-green-500" /> : <TrendingDown className="w-4 h-4 text-red-500" />}
                      </div>
                    } 
                  />
                </div>
                
                {/* Right Side - Action Buttons */}
                <div className="flex gap-3 flex-1 justify-end">
                  <ActionButton 
                    onClick={() => onViewReport(report)} 
                    icon={<Eye className="w-4 h-4" />} 
                    text="View" 
                    className="text-purple-600 hover:bg-purple-50" 
                  />
                  <ActionButton 
                    onClick={() => onDownloadReport(report)} 
                    icon={<Download className="w-4 h-4" />} 
                    text="Download" 
                    className="text-gray-600 hover:bg-gray-50" 
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AssessmentHistory;
